import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Language } from '../types/language';
import { detectUserLocation, getLanguageFromCountry } from '../services/GeoService';
import { translations } from '../translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  userCountry: string | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');
  const [userCountry, setUserCountry] = useState<string | null>(null);

  useEffect(() => {
    const detectLanguage = async () => {
      try {
        // First try to get language from localStorage
        const savedLanguage = localStorage.getItem('preferred-language') as Language;
        if (savedLanguage && Object.keys(translations).includes(savedLanguage)) {
          setLanguage(savedLanguage);
          return;
        }

        // If no saved language, detect from location
        const geoData = await detectUserLocation();
        setUserCountry(geoData.country_code);
        const detectedLanguage = getLanguageFromCountry(geoData.country_code);
        setLanguage(detectedLanguage);
        localStorage.setItem('preferred-language', detectedLanguage);
      } catch (error) {
        console.warn('Failed to detect language:', error);
      }
    };

    detectLanguage();
  }, []);

  const handleSetLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    localStorage.setItem('preferred-language', newLanguage);
    document.documentElement.lang = newLanguage;
  };

  const t = (key: string): string => {
    const translation = translations[language][key as keyof typeof translations['en']];
    if (!translation) {
      console.warn(`Missing translation for key: ${key} in language: ${language}`);
      return translations['en'][key as keyof typeof translations['en']] || key;
    }
    return translation;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage: handleSetLanguage, 
      t,
      userCountry 
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}